import React, { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAvailableLaptops } from '@/services/laptops';
import { fetchAccessories, fetchAllActiveAccessories } from '@/services/accessories';
import {
  fetchCustomers,
  quickCreateCustomer,
  fetchCustomerByExactPhoneOrEmail,
  completeSaleRpc,
  CompleteSalePayload,
  LaptopComponentAdjustmentPayload,
} from '@/services/sales';
import { groupLaptops, formatGroupPriceRange } from '@/lib/laptopGrouping';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { useAuth } from '@/context/AuthContext';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Laptop, Accessory } from '@/types/models';
import {
  ShoppingCart,
  Trash2,
  UserPlus,
  Search,
  Laptop as LaptopIcon,
  Package,
  Wrench,
  Check,
  Loader2,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Sliders,
  AlertCircle,
  QrCode,
} from 'lucide-react';
import { detectComponentType, resolveSaleHardware } from '@/lib/saleHardware';
import { LaptopQrScannerModal } from './LaptopQrScannerModal';

export interface CartComponentAdjustment {
  component_type: 'ram' | 'storage';
  direction: 'installed' | 'recovered';
  accessory_id: string;
  accessory_name: string;
  accessory_sku: string;
  quantity: number;
  unit_cost: number;
  available_qty: number;
}

interface CartItem {
  id: string; // temp unique cart id
  type: 'laptop' | 'accessory' | 'service';
  laptop_id?: string;
  accessory_id?: string;
  laptop_raw?: Laptop;
  name: string;
  sku_or_serial?: string;
  final_ram?: string;
  final_storage?: string;
  clear_ram?: boolean;
  clear_storage?: boolean;
  component_adjustments?: CartComponentAdjustment[];
  available_qty?: number;
  quantity: number;
  unit_price: number;
  unit_cost?: number; // base cost
  line_discount: number;
  complimentary?: boolean;
}

interface NewSaleFormProps {
  onSuccess: (saleId: string) => void;
  onCancel: () => void;
}

const RAM_PRESETS = ['None', '8GB', '16GB', '32GB', '64GB', '128GB'];
const STORAGE_PRESETS = ['None', '256GB SSD', '512GB SSD', '1TB NVMe', '2TB NVMe', '4TB NVMe'];

export const NewSaleForm: React.FC<NewSaleFormProps> = ({ onSuccess, onCancel }) => {
  const { hasPermission, role } = useAuth();
  const canViewCosts = hasPermission('purchases');
  const canAddComplimentary = role === 'owner' || role === 'manager';
  const queryClient = useQueryClient();

  // State
  const [customerMode, setCustomerMode] = useState<'customer' | 'walk_in' | null>(null);
  const [customerId, setCustomerId] = useState<string>('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [newCustomerWhatsApp, setNewCustomerWhatsApp] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [newCustomerAddress, setNewCustomerAddress] = useState('');
  const [newCustomerNotes, setNewCustomerNotes] = useState('');
  const [duplicateCustomerMatch, setDuplicateCustomerMatch] = useState<{ id: string; name: string; phone: string | null; email: string | null } | null>(null);
  const [customerCreating, setCustomerCreating] = useState(false);

  // Items search
  const [laptopSearch, setLaptopSearch] = useState('');
  const [accessorySearch, setAccessorySearch] = useState('');
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string>>(new Set());
  const [expandedAdjustmentIds, setExpandedAdjustmentIds] = useState<Set<string>>(new Set());
  const [showQrScanner, setShowQrScanner] = useState(false);

  // Cart
  const [cart, setCart] = useState<CartItem[]>([]);
  const [overallDiscount, setOverallDiscount] = useState<number>(0);
  const [tax, setTax] = useState<number>(0);
  const [warrantyMonths, setWarrantyMonths] = useState<number>(1);
  const [paymentAmount, setPaymentAmount] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('cash');
  const [notes, setNotes] = useState<string>('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Queries
  const { data: customers = [], error: customersError, refetch: retryCustomers } = useQuery({
    queryKey: ['customers', 'select', customerSearch],
    queryFn: () => fetchCustomers(customerSearch),
  });

  const { data: availableLaptops = [], error: laptopsError, refetch: retryLaptops } = useQuery({
    queryKey: ['laptops', 'available', role, laptopSearch],
    queryFn: () => fetchAvailableLaptops(laptopSearch, role === 'staff'),
  });

  const { data: accessoriesData, error: accessoriesError, refetch: retryAccessories } = useQuery({
    queryKey: ['accessories', 'available', role, accessorySearch],
    queryFn: () => fetchAccessories({ search: accessorySearch, page: 0, isStaff: role === 'staff' }),
  });
  const availableAccessories = accessoriesData?.data ?? [];
  const { data: componentAccessories = [], error: componentError, isLoading: loadingComponents, refetch: retryComponents } = useQuery({
    queryKey: ['accessories', 'checkout-components', role],
    queryFn: fetchAllActiveAccessories,
    enabled: canViewCosts,
  });

  // Group available laptops by base machine / model
  const groupedAvailableLaptops = useMemo(() => {
    return groupLaptops(availableLaptops);
  }, [availableLaptops]);

  const toggleGroup = (key: string) => {
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAdjustmentPanel = (cartItemId: string) => {
    setExpandedAdjustmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(cartItemId)) next.delete(cartItemId);
      else next.add(cartItemId);
      return next;
    });
  };

  // Check duplicate when typing phone or email in Quick Add
  const handlePhoneOrEmailChange = async (field: 'phone' | 'email', val: string) => {
    if (field === 'phone') setNewCustomerPhone(val);
    if (field === 'email') setNewCustomerEmail(val);

    const testPhone = field === 'phone' ? val : newCustomerPhone;
    const testEmail = field === 'email' ? val : newCustomerEmail;

    if (testPhone.trim().length >= 7 || (testEmail.trim().includes('@') && testEmail.trim().length >= 5)) {
      const match = await fetchCustomerByExactPhoneOrEmail({ phone: testPhone, email: testEmail });
      setDuplicateCustomerMatch(match);
    } else {
      setDuplicateCustomerMatch(null);
    }
  };

  // Quick Customer Create
  const handleQuickCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCustomerName.trim()) return;

    if (duplicateCustomerMatch) {
      setSubmitError(
        `A customer with this contact information already exists ("${duplicateCustomerMatch.name}"). Click "Select Existing" above or modify the phone/email before creating a new record.`
      );
      return;
    }

    try {
      setCustomerCreating(true);
      setSubmitError(null);

      if (newCustomerPhone.trim().length >= 7 || (newCustomerEmail.trim().includes('@') && newCustomerEmail.trim().length >= 5)) {
        const existingMatch = await fetchCustomerByExactPhoneOrEmail({
          phone: newCustomerPhone.trim(),
          email: newCustomerEmail.trim(),
        });
        if (existingMatch) {
          setDuplicateCustomerMatch(existingMatch);
          setSubmitError(
            `A customer with this contact information already exists ("${existingMatch.name}"). Click "Select Existing" above or modify the phone/email before creating a new record.`
          );
          return;
        }
      }

      const created = await quickCreateCustomer({
        name: newCustomerName,
        phone: newCustomerPhone || null,
        whatsapp: newCustomerWhatsApp || null,
        email: newCustomerEmail || null,
        address: newCustomerAddress || null,
        notes: newCustomerNotes || null,
      });

      // Update customer query cache so the select menu immediately renders and selects the customer
      queryClient.setQueryData(['customers', 'select', ''], (old: any[] = []) => [created, ...old]);
      queryClient.setQueryData(['customers', 'select', customerSearch], (old: any[] = []) => [created, ...old]);
      setCustomerMode('customer');
      setCustomerId(created.id);
      setShowQuickCustomer(false);
      setNewCustomerName('');
      setNewCustomerPhone('');
      setNewCustomerWhatsApp('');
      setNewCustomerEmail('');
      setNewCustomerAddress('');
      setNewCustomerNotes('');
      setDuplicateCustomerMatch(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
    } catch (err) {
      setSubmitError(parseDatabaseError(err));
    } finally {
      setCustomerCreating(false);
    }
  };

  // Cart Handlers
  const addLaptopToCart = (laptop: Laptop) => {
    if (cart.some((item) => item.laptop_id === laptop.id)) return;
    setCart((prev) => [
      ...prev,
      {
        id: `laptop-${laptop.id}`,
        type: 'laptop',
        laptop_id: laptop.id,
        laptop_raw: laptop,
        name: `${laptop.brand} ${laptop.model}`,
        sku_or_serial: laptop.stock_id + (laptop.serial_number ? ` • S/N: ${laptop.serial_number}` : ''),
        final_ram: laptop.ram || '',
        final_storage: laptop.storage || '',
        component_adjustments: [],
        quantity: 1,
        unit_price: laptop.asking_price ?? 0,
        unit_cost: laptop.current_cost_basis ?? laptop.landed_cost ?? laptop.purchase_price ?? 0,
        line_discount: 0,
      },
    ]);
  };

  const addAccessoryToCart = (acc: Accessory) => {
    const existing = cart.find((item) => item.accessory_id === acc.id);
    if (existing) {
      if (existing.quantity >= acc.quantity) return;
      setCart((prev) =>
        prev.map((item) =>
          item.accessory_id === acc.id ? { ...item, quantity: item.quantity + 1 } : item
        )
      );
    } else {
      setCart((prev) => [
        ...prev,
        {
          id: `acc-${acc.id}`,
          type: 'accessory',
          accessory_id: acc.id,
          name: acc.name,
          sku_or_serial: acc.sku,
          available_qty: acc.quantity,
          quantity: 1,
          unit_price: acc.selling_price ?? 0,
          unit_cost: acc.average_cost ?? 0,
          line_discount: 0,
        },
      ]);
    }
  };

  const addServiceToCart = () => {
    const serviceId = `service-${Date.now()}`;
    setCart((prev) => [
      ...prev,
      {
        id: serviceId,
        type: 'service',
        name: 'Service / Labor Charge',
        quantity: 1,
        unit_price: 1000,
        unit_cost: 0,
        line_discount: 0,
      },
    ]);
  };

  const updateCartItem = (id: string, updates: Partial<CartItem>) => {
    setCart((prev) => prev.map((item) => (item.id === id ? { ...item, ...updates } : item)));
  };

  const removeCartItem = (id: string) => {
    setCart((prev) => prev.filter((item) => item.id !== id));
  };

  // Component adjustments helper on cart item
  const addComponentAdjustment = (
    cartItemId: string,
    adj: {
      component_type: 'ram' | 'storage';
      direction: 'installed' | 'recovered';
      accessory: Accessory;
      quantity?: number;
    }
  ) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id !== cartItemId) return item;
        const currentAdjs = item.component_adjustments || [];
        const newAdjustment: CartComponentAdjustment = {
          component_type: adj.component_type,
          direction: adj.direction,
          accessory_id: adj.accessory.id,
          accessory_name: adj.accessory.name,
          accessory_sku: adj.accessory.sku,
          quantity: adj.quantity || 1,
          unit_cost: adj.accessory.average_cost,
          available_qty: adj.accessory.quantity,
        };
        return {
          ...item,
          component_adjustments: [...currentAdjs, newAdjustment],
        };
      })
    );
  };

  const removeComponentAdjustment = (cartItemId: string, indexToRemove: number) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.id !== cartItemId) return item;
        const currentAdjs = item.component_adjustments || [];
        return {
          ...item,
          component_adjustments: currentAdjs.filter((_, idx) => idx !== indexToRemove),
        };
      })
    );
  };

  // Calculate adjusted COGS for laptop cart items
  const getItemFinalCOGS = (item: CartItem): number => {
    const baseCost = item.unit_cost ?? 0;
    if (item.type !== 'laptop' || !item.component_adjustments?.length) {
      return baseCost;
    }
    const installedCost = item.component_adjustments
      .filter((a) => a.direction === 'installed')
      .reduce((sum, a) => sum + a.quantity * a.unit_cost, 0);
    const recoveredCredit = item.component_adjustments
      .filter((a) => a.direction === 'recovered')
      .reduce((sum, a) => sum + a.quantity * a.unit_cost, 0);
    return baseCost + installedCost - recoveredCredit;
  };

  // Totals calculations
  const subtotal = useMemo(() => {
    return cart.reduce((sum, item) => {
      const lineTotal = item.quantity * item.unit_price - item.line_discount;
      return sum + Math.max(0, lineTotal);
    }, 0);
  }, [cart]);

  const totalCost = useMemo(() => {
    return cart.reduce((sum, item) => {
      const finalLineCOGS = getItemFinalCOGS(item);
      return sum + item.quantity * finalLineCOGS;
    }, 0);
  }, [cart]);

  const total = Math.max(0, subtotal - overallDiscount + tax);
  // Benchmark rule: gross profit excludes tax
  const netMerchandiseRevenue = Math.max(0, subtotal - overallDiscount);
  const estProfit = netMerchandiseRevenue - totalCost;

  // Complete Sale Mutation
  const completeMutation = useMutation({
    mutationFn: async () => {
      if (cart.length === 0) throw new Error('Cart cannot be empty.');
      const numericPayment = paymentAmount === '' ? null : Number(paymentAmount);

      // Verify no negative cost basis on any laptop
      for (const item of cart) {
        if (item.type === 'laptop') {
          const finalCogs = getItemFinalCOGS(item);
          if (finalCogs < 0) {
            throw new Error(`Negative cost basis detected on laptop ${item.sku_or_serial || item.name} (Rs. ${finalCogs}). Recovered parts credit cannot exceed laptop cost plus installed parts.`);
          }
        }
      }

      const itemsPayload: CompleteSalePayload['items'] = cart.map((item) => {
        if (item.type === 'laptop') {
          const compAdjustmentsPayload: LaptopComponentAdjustmentPayload[] = (item.component_adjustments || []).map((a) => ({
            component_type: a.component_type,
            direction: a.direction,
            accessory_id: a.accessory_id,
            quantity: a.quantity,
            unit_cost: a.unit_cost,
          }));

          const hardware = resolveSaleHardware({
            label: item.sku_or_serial || item.name,
            originalRam: item.laptop_raw?.ram, originalStorage: item.laptop_raw?.storage,
            finalRam: item.final_ram, finalStorage: item.final_storage,
            clearRam: item.clear_ram, clearStorage: item.clear_storage,
            actions: item.component_adjustments || [],
          });

          return {
            type: 'laptop' as const,
            id: item.laptop_id!,
            unit_price: item.unit_price,
            line_discount: item.line_discount,
            ...hardware,
            component_adjustments: compAdjustmentsPayload.length > 0 ? compAdjustmentsPayload : undefined,
          };
        }
        if (item.type === 'accessory') {
          return {
            type: 'accessory' as const,
            id: item.accessory_id!,
            quantity: item.quantity,
            unit_price: item.complimentary ? 0 : item.unit_price,
            line_discount: item.complimentary ? 0 : item.line_discount,
            complimentary: item.complimentary ?? false,
          };
        }
        return {
          type: 'service' as const,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          unit_cost: item.unit_cost,
          line_discount: item.line_discount,
        };
      });

      if (customerMode === null) {
        throw new Error('Select a customer or choose Walk-in / Cash before completing the sale.');
      }
      if (customerMode === 'customer' && !customerId) {
        throw new Error('Please select a registered customer or choose Walk-in / Cash.');
      }

      const res = await completeSaleRpc({
        customer_id: customerMode === 'customer' ? (customerId || null) : null,
        walk_in_confirmed: customerMode === 'walk_in',
        items: itemsPayload,
        discount: overallDiscount,
        tax,
        payment_amount: numericPayment,
        payment_method: paymentMethod,
        notes: notes || null,
        warranty_months: warrantyMonths,
      });

      return res;
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.sales.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      onSuccess(res.sale_id);
    },
    onError: (err: unknown) => {
      setSubmitError(parseDatabaseError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (customerMode === null) {
      setSubmitError('Select a customer or choose Walk-in / Cash before completing the sale.');
      return;
    }
    if (customerMode === 'customer' && !customerId) {
      setSubmitError('Please select a registered customer or choose Walk-in / Cash.');
      return;
    }

    completeMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[85vh] overflow-y-auto pr-1 text-xs">
      {submitError && <ErrorAlert message={submitError} />}
      {customersError && <ErrorAlert message={`Unable to load customers: ${parseDatabaseError(customersError)}`} onRetry={() => { void retryCustomers(); }} />}
      {laptopsError && <ErrorAlert message={`Unable to load laptops: ${parseDatabaseError(laptopsError)}`} onRetry={() => { void retryLaptops(); }} />}
      {accessoriesError && <ErrorAlert message={`Unable to load accessories: ${parseDatabaseError(accessoriesError)}`} onRetry={() => { void retryAccessories(); }} />}

      {/* ── TOP CUSTOMER BAR ── */}
      <div className={`p-3 rounded-lg border space-y-2.5 transition-colors ${
        customerMode === null ? 'bg-amber-50/60 border-amber-300' : 'bg-brand-bg border-brand-border'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center space-x-2">
            <label className="font-bold text-brand-black uppercase tracking-wider text-xs">
              Customer Selection <span className="text-red-500 font-bold">*</span>
            </label>
            {customerMode === null && (
              <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-[10px] font-semibold rounded border border-amber-300">
                Action Required: Choose Customer or Walk-in
              </span>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {/* Explicit Mode Buttons */}
            <button
              type="button"
              onClick={() => {
                setCustomerMode('walk_in');
                setCustomerId('');
              }}
              className={`px-3 py-1 text-xs font-semibold rounded-md border transition-all ${
                customerMode === 'walk_in'
                  ? 'bg-brand-black text-white border-brand-black shadow-xs'
                  : 'bg-white text-brand-black border-brand-border hover:bg-gray-50'
              }`}
            >
              ✓ Walk-in / Cash
            </button>

            <button
              type="button"
              onClick={() => setShowQuickCustomer(!showQuickCustomer)}
              className="inline-flex items-center space-x-1 text-brand-green font-bold hover:underline text-xs"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>+ Quick Add Customer</span>
            </button>
          </div>
        </div>

        {showQuickCustomer && (
          <div className="bg-white p-3 rounded-lg border border-brand-green/30 space-y-2 shadow-xs">
            <div className="flex items-center justify-between border-b pb-1">
              <span className="font-bold text-brand-black text-xs">Create & Select New Customer</span>
              <button
                type="button"
                onClick={() => setShowQuickCustomer(false)}
                className="text-brand-muted hover:text-brand-black text-[10px]"
              >
                ✕ Close
              </button>
            </div>

            {duplicateCustomerMatch && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded flex items-center justify-between text-amber-900 text-[11px]">
                <div className="flex items-center space-x-1.5">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span>
                    Existing customer match: <strong>{duplicateCustomerMatch.name}</strong> ({duplicateCustomerMatch.phone || duplicateCustomerMatch.email})
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setCustomerMode('customer');
                    setCustomerId(duplicateCustomerMatch.id);
                    setShowQuickCustomer(false);
                    setDuplicateCustomerMatch(null);
                  }}
                  className="px-2 py-0.5 bg-amber-600 text-white font-bold rounded text-[10px] hover:bg-amber-700"
                >
                  Select Existing
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                placeholder="Customer Full Name *"
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                className="px-3 py-1.5 border rounded text-xs"
              />
              <input
                placeholder="Phone (e.g. 03001234567)"
                value={newCustomerPhone}
                onChange={(e) => handlePhoneOrEmailChange('phone', e.target.value)}
                className="px-3 py-1.5 border rounded text-xs"
              />
              <input
                placeholder="WhatsApp (Optional, e.g. 03001234567)"
                value={newCustomerWhatsApp}
                onChange={(e) => setNewCustomerWhatsApp(e.target.value)}
                className="px-3 py-1.5 border rounded text-xs"
              />
              <input
                placeholder="Email Address (Optional)"
                value={newCustomerEmail}
                onChange={(e) => handlePhoneOrEmailChange('email', e.target.value)}
                className="px-3 py-1.5 border rounded text-xs"
              />
              <input
                placeholder="City / Address (e.g. Rawalpindi)"
                value={newCustomerAddress}
                onChange={(e) => setNewCustomerAddress(e.target.value)}
                className="px-3 py-1.5 border rounded text-xs sm:col-span-2"
              />
            </div>
            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={handleQuickCustomer}
                disabled={customerCreating || !newCustomerName.trim()}
                className="px-4 py-1.5 bg-brand-green text-white font-bold rounded text-xs hover:bg-brand-green-hover disabled:opacity-50 flex items-center space-x-1"
              >
                {customerCreating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                <span>{customerCreating ? 'Saving...' : 'Save & Select Customer'}</span>
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="relative">
            <input
              placeholder="Search customer by name, phone, or email..."
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-white border border-brand-border rounded text-xs focus:ring-2 focus:ring-brand-green"
            />
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-brand-muted" />
          </div>
          <select
            value={customerMode === 'walk_in' ? '__walk_in__' : customerId}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '__walk_in__') {
                setCustomerMode('walk_in');
                setCustomerId('');
              } else if (val) {
                setCustomerMode('customer');
                setCustomerId(val);
              } else {
                setCustomerMode(null);
                setCustomerId('');
              }
            }}
            className={`w-full px-3 py-1.5 bg-white border rounded text-xs font-semibold ${
              customerMode === null ? 'border-amber-400 text-amber-900 bg-amber-50/50' : 'border-brand-border text-brand-black'
            }`}
          >
            <option value="">-- Choose a registered customer or Walk-in --</option>
            <option value="__walk_in__">Walk-in Customer / Cash Sale</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} {c.phone ? `(${c.phone})` : ''} {c.address ? `• ${c.address}` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── MAIN 2-COLUMN POS LAYOUT ── */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        {/* ── LEFT COLUMN: INVENTORY CATALOG (7 COLS) ── */}
        <div className="xl:col-span-7 space-y-3">
          {/* Grouped Available Laptops Section */}
          <div className="bg-white p-3.5 rounded-lg border border-brand-border space-y-2.5 shadow-xs">
            <div className="flex items-center justify-between border-b pb-2">
              <div className="flex items-center space-x-2">
                <h4 className="font-bold text-brand-black flex items-center space-x-1.5 text-sm">
                  <LaptopIcon className="w-4 h-4 text-brand-green" />
                  <span>Available Laptops</span>
                </h4>
                <span className="text-[11px] text-brand-muted font-medium hidden sm:inline">
                  ({availableLaptops.length} in stock)
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowQrScanner(true)}
                className="inline-flex items-center space-x-1 px-2.5 py-1 bg-brand-green text-white text-xs font-bold rounded-md hover:bg-brand-green-hover transition-colors shadow-xs"
                title="Scan physical laptop QR code to add directly to sale"
              >
                <QrCode className="w-3.5 h-3.5" />
                <span>Scan Laptop QR</span>
              </button>
            </div>

            <div className="relative">
              <input
                placeholder="Search laptop brand, model, CPU, serial, stock ID..."
                value={laptopSearch}
                onChange={(e) => setLaptopSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-brand-bg border border-brand-border rounded text-xs focus:ring-2 focus:ring-brand-green"
              />
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-brand-muted" />
            </div>

            {/* Model Groups Accordion List */}
            <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
              {groupedAvailableLaptops.length === 0 ? (
                <p className="p-4 text-center text-brand-muted italic">No sellable laptops in stock</p>
              ) : (
                groupedAvailableLaptops.map((group) => {
                  const isExpanded = expandedGroupKeys.has(group.key);
                  const inCartCount = group.units.filter((u) => cart.some((item) => item.laptop_id === u.id)).length;
                  const availableCount = group.units.length - inCartCount;

                  return (
                    <div
                      key={group.key}
                      className="border border-brand-border rounded-md overflow-hidden bg-white shadow-2xs"
                    >
                      {/* Model Group Header Row */}
                      <div
                        onClick={() => toggleGroup(group.key)}
                        className="p-2.5 bg-slate-50 hover:bg-slate-100/80 cursor-pointer flex items-center justify-between transition-colors select-none"
                      >
                        <div className="flex items-center space-x-2 flex-1">
                          <span className="p-1 bg-brand-green/10 text-brand-green rounded">
                            {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                          </span>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-bold text-brand-black text-xs">
                                {group.brand} {group.model}
                              </span>
                              {group.touchscreen === true && (
                                <span className="px-1.5 py-0.2 bg-blue-100 text-blue-800 text-[9px] font-bold rounded">
                                  Touch
                                </span>
                              )}
                              {group.touchscreen === false && (
                                <span className="px-1.5 py-0.2 bg-slate-100 text-slate-700 text-[9px] font-bold rounded">
                                  Non-Touch
                                </span>
                              )}
                            </div>
                            <p className="text-[10px] text-brand-muted font-mono">
                              {group.cpu || 'Base CPU'} • Asking: {formatGroupPriceRange(group.minPrice, group.maxPrice)}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <span
                            className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${
                              availableCount > 0
                                ? 'bg-green-100 text-green-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {availableCount} Available ({group.units.length} total)
                          </span>
                        </div>
                      </div>

                      {/* Expanded Serialized Units */}
                      {isExpanded && (
                        <div className="divide-y divide-brand-border bg-white border-t border-brand-border">
                          {group.units.map((laptop) => {
                            const isAdded = cart.some((item) => item.laptop_id === laptop.id);
                            return (
                              <div
                                key={laptop.id}
                                className={`p-2 flex items-center justify-between transition-colors ${
                                  isAdded ? 'bg-green-50/50' : 'hover:bg-brand-bg'
                                }`}
                              >
                                <div className="space-y-0.5">
                                  <div className="flex items-center space-x-2">
                                    <span className="font-bold text-brand-black text-xs font-mono">
                                      {laptop.stock_id}
                                    </span>
                                    {laptop.serial_number && (
                                      <span className="text-[10px] text-brand-muted font-mono">
                                        S/N: {laptop.serial_number}
                                      </span>
                                    )}
                                    <span className="px-1.5 py-0.2 bg-brand-bg text-brand-black text-[9px] font-semibold rounded uppercase">
                                      {laptop.condition}
                                    </span>
                                  </div>
                                  <p className="text-[10px] text-brand-muted">
                                    {laptop.ram || '16GB'} • {laptop.storage || '512GB SSD'}
                                    {laptop.gpu ? ` • ${laptop.gpu}` : ''}
                                  </p>
                                </div>

                                <div className="flex items-center space-x-2">
                                  <span className="font-bold text-brand-green text-xs">
                                    Rs. {(laptop.asking_price ?? 0).toLocaleString('en-PK')}
                                  </span>
                                  <button
                                    type="button"
                                    disabled={isAdded}
                                    onClick={() => addLaptopToCart(laptop)}
                                    className={`px-2.5 py-1 text-xs font-bold rounded transition-colors ${
                                      isAdded
                                        ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                                        : 'bg-brand-green text-white hover:bg-brand-green-hover shadow-xs'
                                    }`}
                                  >
                                    {isAdded ? 'In Cart' : '+ Add'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Accessories & Custom Service Section */}
          <div className="bg-white p-3.5 rounded-lg border border-brand-border space-y-2.5 shadow-xs">
            <div className="flex items-center justify-between border-b pb-2">
              <h4 className="font-bold text-brand-black flex items-center space-x-1.5 text-sm">
                <Package className="w-4 h-4 text-blue-600" />
                <span>Accessories & Component Stock</span>
              </h4>
              <button
                type="button"
                onClick={addServiceToCart}
                className="text-xs font-bold text-purple-700 hover:underline flex items-center space-x-1"
              >
                <Wrench className="w-3.5 h-3.5" />
                <span>+ Custom Service / Labor</span>
              </button>
            </div>

            <div className="relative">
              <input
                placeholder="Search accessory name, SKU, category..."
                value={accessorySearch}
                onChange={(e) => setAccessorySearch(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-brand-bg border border-brand-border rounded text-xs focus:ring-2 focus:ring-brand-green"
              />
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-brand-muted" />
            </div>

            <div className="max-h-48 overflow-y-auto divide-y divide-brand-border border rounded">
              {availableAccessories.length === 0 ? (
                <p className="p-3 text-center text-brand-muted italic">No active accessories in inventory</p>
              ) : (
                availableAccessories.map((acc) => {
                  const itemInCart = cart.find((i) => i.accessory_id === acc.id);
                  const currentQtyInCart = itemInCart?.quantity ?? 0;
                  const outOfStock = acc.quantity <= currentQtyInCart;

                  return (
                    <div key={acc.id} className="p-2 flex items-center justify-between hover:bg-brand-bg transition-colors">
                      <div>
                        <p className="font-semibold text-brand-black text-xs">{acc.name}</p>
                        <p className="text-[10px] text-brand-muted">
                          SKU: {acc.sku} • In Stock: {acc.quantity}
                          {canViewCosts && ` • Cost: Rs. ${acc.average_cost.toLocaleString('en-PK')}`}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-brand-green text-xs">
                          Rs. {acc.selling_price.toLocaleString('en-PK')}
                        </span>
                        <button
                          type="button"
                          disabled={outOfStock}
                          onClick={() => addAccessoryToCart(acc)}
                          className="px-2.5 py-1 bg-blue-600 text-white font-bold rounded text-xs hover:bg-blue-700 disabled:opacity-50"
                        >
                          {outOfStock ? 'No Stock' : '+ Add'}
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT COLUMN: CART & CUSTOM CONFIGURATION (5 COLS) ── */}
        <div className="xl:col-span-5 bg-white p-4 rounded-lg border border-brand-border flex flex-col justify-between space-y-4 shadow-sm">
          <div>
            <div className="flex items-center justify-between border-b pb-2 mb-3">
              <h4 className="font-bold text-brand-black flex items-center space-x-1.5 text-sm">
                <ShoppingCart className="w-4 h-4 text-brand-green" />
                <span>Invoice Items ({cart.length})</span>
              </h4>
              {cart.length > 0 && (
                <button
                  type="button"
                  onClick={() => setCart([])}
                  className="text-brand-red font-semibold hover:underline text-xs"
                >
                  Clear Cart
                </button>
              )}
            </div>

            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center text-brand-muted">
                <ShoppingCart className="w-10 h-10 opacity-20 mb-2" />
                <p className="font-bold text-xs text-brand-black">Cart is empty</p>
                <p className="text-[11px] text-brand-muted mt-1">
                  Select available laptops or accessories on the left to add them.
                </p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[380px] overflow-y-auto pr-1">
                {cart.map((item) => {
                  const finalLineCOGS = getItemFinalCOGS(item);
                  const isAdjustmentOpen = expandedAdjustmentIds.has(item.id);
                  const laptopAdjs = item.component_adjustments || [];
                  const netAdjValue = laptopAdjs
                    .filter((a) => a.direction === 'installed')
                    .reduce((sum, a) => sum + a.quantity * a.unit_cost, 0) -
                    laptopAdjs
                      .filter((a) => a.direction === 'recovered')
                      .reduce((sum, a) => sum + a.quantity * a.unit_cost, 0);

                  return (
                    <div
                      key={item.id}
                      className="p-3 bg-brand-bg rounded-lg border border-brand-border space-y-2 shadow-xs"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-bold text-brand-black text-xs flex items-center space-x-1.5">
                            {item.type === 'laptop' && <LaptopIcon className="w-4 h-4 text-brand-green shrink-0" />}
                            {item.type === 'accessory' && <Package className="w-4 h-4 text-blue-600 shrink-0" />}
                            {item.type === 'service' && <Wrench className="w-4 h-4 text-purple-600 shrink-0" />}
                            {item.type === 'service' ? (
                              <input
                                value={item.name}
                                onChange={(e) => updateCartItem(item.id, { name: e.target.value })}
                                className="bg-white px-2 py-0.5 border rounded text-xs font-bold flex-1"
                              />
                            ) : (
                              <span>{item.name}</span>
                            )}
                          </div>
                          {item.sku_or_serial && (
                            <p className="text-[10px] text-brand-muted font-mono mt-0.5">{item.sku_or_serial}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeCartItem(item.id)}
                          className="text-brand-red hover:text-red-700 p-1"
                          title="Remove item"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {/* ── LAPTOP FINAL SOLD CONFIGURATION (RAM / SSD / PRICE) ── */}
                      {item.type === 'laptop' && (
                        <div className="p-2.5 bg-white rounded border border-brand-border space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-brand-green uppercase tracking-wider flex items-center space-x-1">
                              <Sparkles className="w-3 h-3" />
                              <span>Final Sold Spec Snapshot</span>
                            </span>
                            <span className="text-[9px] text-brand-muted">
                              Recorded: {item.laptop_raw?.ram || 'RAM not recorded'} • {item.laptop_raw?.storage || 'Storage not recorded'}
                            </span>
                          </div>

                          {/* Final Sold RAM */}
                          <div>
                            <label className="block text-[10px] font-bold text-brand-black uppercase mb-1">
                              Final RAM:
                            </label>
                            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                              {RAM_PRESETS.map((preset) => (
                                <button
                                  key={preset}
                                  type="button"
                                  onClick={() => updateCartItem(item.id, { final_ram: preset, clear_ram: false })}
                                  aria-label={`Final RAM: ${preset}`}
                                  aria-pressed={item.final_ram === preset}
                                  className={`px-2 py-0.5 text-[10px] font-bold rounded border transition-colors ${
                                    item.final_ram === preset
                                      ? 'bg-brand-green text-white border-brand-green'
                                      : 'bg-brand-bg text-brand-black border-brand-border hover:bg-slate-200'
                                  }`}
                                >
                                  {preset}
                                </button>
                              ))}
                            </div>
                            <input
                              type="text"
                              placeholder="e.g. 16GB DDR4"
                              value={item.final_ram || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                updateCartItem(item.id, {
                                  final_ram: val,
                                  clear_ram: val.trim() === '' && Boolean(item.laptop_raw?.ram),
                                });
                              }}
                              className="w-full px-2 py-1 bg-brand-bg border rounded text-xs font-semibold"
                            />
                          </div>

                          {/* Final Sold Storage */}
                          <div>
                            <label className="block text-[10px] font-bold text-brand-black uppercase mb-1">
                              Final SSD / Storage:
                            </label>
                            <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                              {STORAGE_PRESETS.map((preset) => (
                                <button
                                  key={preset}
                                  type="button"
                                  onClick={() => updateCartItem(item.id, { final_storage: preset, clear_storage: false })}
                                  aria-label={`Final storage: ${preset}`}
                                  aria-pressed={item.final_storage === preset}
                                  className={`px-2 py-0.5 text-[10px] font-bold rounded border transition-colors ${
                                    item.final_storage === preset
                                      ? 'bg-brand-green text-white border-brand-green'
                                      : 'bg-brand-bg text-brand-black border-brand-border hover:bg-slate-200'
                                  }`}
                                >
                                  {preset}
                                </button>
                              ))}
                            </div>
                            <input
                              type="text"
                              placeholder="e.g. 512GB NVMe"
                              value={item.final_storage || ''}
                              onChange={(e) => {
                                const val = e.target.value;
                                updateCartItem(item.id, {
                                  final_storage: val,
                                  clear_storage: val.trim() === '' && Boolean(item.laptop_raw?.storage),
                                });
                              }}
                              className="w-full px-2 py-1 bg-brand-bg border rounded text-xs font-semibold"
                            />
                          </div>

                          {/* ── HARDWARE COMPONENT STOCK ADJUSTMENT PANEL ── */}
                          <div className="pt-2 border-t border-slate-200">
                            <button
                              type="button"
                              onClick={() => toggleAdjustmentPanel(item.id)}
                              className="w-full flex items-center justify-between text-[11px] font-bold text-blue-700 hover:text-blue-900 bg-blue-50/60 p-1.5 rounded border border-blue-200 transition-colors"
                            >
                              <span className="flex items-center space-x-1">
                                <Sliders className="w-3.5 h-3.5" />
                                <span>Component Stock & Cost Adjustment ({laptopAdjs.length})</span>
                              </span>
                              <span>{isAdjustmentOpen ? '▲ Hide' : '▼ Configure Hardware'}</span>
                            </button>

                            {isAdjustmentOpen && (
                              <div className="mt-2 p-2 bg-slate-50 rounded border border-slate-200 space-y-2 text-[11px]">
                                <div className="flex items-center justify-between text-slate-700">
                                  <span className="font-semibold">Select physical components installed / removed:</span>
                                </div>

                                {/* Active Component Adjustments Table */}
                                {laptopAdjs.length > 0 && (
                                  <div className="space-y-1 bg-white p-2 rounded border">
                                    {laptopAdjs.map((adj, idx) => (
                                      <div
                                        key={idx}
                                        className="flex items-center justify-between p-1 bg-slate-50 rounded text-[10px]"
                                      >
                                        <div className="flex items-center space-x-1.5">
                                          <span
                                            className={`px-1.5 py-0.2 font-bold rounded uppercase ${
                                              adj.direction === 'installed'
                                                ? 'bg-blue-100 text-blue-800'
                                                : 'bg-green-100 text-green-800'
                                            }`}
                                          >
                                            {adj.direction === 'installed' ? 'Installed (-Stock)' : 'Recovered (+Stock)'}
                                          </span>
                                          <span className="font-bold text-brand-black">{adj.accessory_name}</span>
                                          <span className="text-brand-muted font-mono">(x{adj.quantity})</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          {canViewCosts && (
                                            <span className="font-mono text-slate-700">
                                              {adj.direction === 'installed' ? '+' : '-'}Rs. {(adj.quantity * adj.unit_cost).toLocaleString('en-PK')}
                                            </span>
                                          )}
                                          <button
                                            type="button"
                                            onClick={() => removeComponentAdjustment(item.id, idx)}
                                            className="text-red-600 hover:text-red-800 font-bold"
                                          >
                                            ✕
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Add Component Selector */}
                                <div className="p-2 bg-white rounded border space-y-1.5">
                                  <span className="font-bold text-[10px] text-brand-black block uppercase">
                                    + Add Component Action
                                  </span>
                                  {!canViewCosts && <p>Ask an owner or manager to record hardware changes.</p>}
                                  {componentError && <ErrorAlert message={parseDatabaseError(componentError)} onRetry={() => { void retryComponents(); }} />}
                                  {loadingComponents && <p>Loading component stock…</p>}
                                  <p className="text-[10px]">Choose None for RAM or Storage when no part will remain installed. Removal only needs a Recover action for the actual part returned to stock; no Install action is required. If the part is already absent, add no action.</p>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                    <select
                                      id={`adj-acc-select-${item.id}`}
                                      className="w-full px-2 py-1 bg-slate-50 border rounded text-[10px] font-semibold"
                                      defaultValue=""
                                      onChange={(e) => {
                                        const part = componentAccessories.find(a => a.id === e.target.value);
                                        const typeSelect = document.getElementById(`adj-type-select-${item.id}`) as HTMLSelectElement;
                                        typeSelect.value = part ? detectComponentType(part) || '' : '';
                                      }}
                                    >
                                      <option value="">Select RAM / SSD Accessory...</option>
                                      {componentAccessories.map((acc) => (
                                        <option key={acc.id} value={acc.id}>
                                          {acc.name} ({acc.sku}) • Stock: {acc.quantity} {canViewCosts ? `• Cost: Rs. ${acc.average_cost}` : ''}
                                        </option>
                                      ))}
                                    </select>

                                    <select id={`adj-type-select-${item.id}`} aria-label="Component type" defaultValue="" className="px-2 py-1 border rounded text-xs">
                                      <option value="">Choose component type…</option>
                                      <option value="ram">RAM</option>
                                      <option value="storage">Storage / SSD / HDD</option>
                                    </select>
                                    <div className="flex items-center space-x-1">
                                      <select
                                        id={`adj-dir-select-${item.id}`}
                                        className="flex-1 px-2 py-1 bg-slate-50 border rounded text-[10px] font-semibold"
                                        defaultValue="installed"
                                      >
                                        <option value="installed">Install (Deduct Stock)</option>
                                        <option value="recovered">Recover to Shop Stock (+Qty)</option>
                                      </select>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const accSelect = document.getElementById(`adj-acc-select-${item.id}`) as HTMLSelectElement;
                                          const dirSelect = document.getElementById(`adj-dir-select-${item.id}`) as HTMLSelectElement;
                                          if (!accSelect.value) return;
                                          const selectedAcc = componentAccessories.find((a) => a.id === accSelect.value);
                                          if (!selectedAcc) return;

                                          const typeSelect = document.getElementById(`adj-type-select-${item.id}`) as HTMLSelectElement;
                                          const compType = typeSelect.value;
                                          if (compType !== 'ram' && compType !== 'storage') { setSubmitError('Choose RAM or Storage for the selected component.'); return; }
                                          const detected = detectComponentType(selectedAcc);
                                          if (detected && detected !== compType) { setSubmitError(`${selectedAcc.name} is a ${detected === 'ram' ? 'RAM' : 'Storage'} part. Choose the matching component type.`); return; }
                                          if ((item.component_adjustments || []).some(a => a.accessory_id === selectedAcc.id && a.direction === dirSelect.value && a.component_type === compType)) { setSubmitError('This action is already listed. Edit its quantity instead.'); return; }
                                          setSubmitError(null);

                                          addComponentAdjustment(item.id, {
                                            component_type: compType,
                                            direction: dirSelect.value as 'installed' | 'recovered',
                                            accessory: selectedAcc,
                                            quantity: 1,
                                          });

                                          accSelect.value = '';
                                          typeSelect.value = '';
                                        }}
                                        className="px-2.5 py-1 bg-blue-600 text-white font-bold rounded text-[10px] hover:bg-blue-700"
                                      >
                                        Add
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                {/* Cost & Gross Profit Breakdown for Laptop */}
                                {canViewCosts && (
                                  <div className="p-2 bg-slate-100 rounded text-[10px] space-y-0.5 font-mono text-slate-700">
                                    <div className="flex justify-between">
                                      <span>Base Landed Cost:</span>
                                      <span>Rs. {(item.unit_cost || 0).toLocaleString('en-PK')}</span>
                                    </div>
                                    <div className="flex justify-between text-blue-800 font-semibold">
                                      <span>Component Net Adjustment:</span>
                                      <span>{netAdjValue >= 0 ? `+ Rs. ${netAdjValue.toLocaleString('en-PK')}` : `- Rs. ${Math.abs(netAdjValue).toLocaleString('en-PK')}`}</span>
                                    </div>
                                    <div className="flex justify-between font-bold border-t pt-0.5 text-brand-black">
                                      <span>Final Laptop COGS:</span>
                                      <span>Rs. {finalLineCOGS.toLocaleString('en-PK')}</span>
                                    </div>
                                    <div className="flex justify-between text-green-700 font-bold">
                                      <span>Expected Line Gross Profit:</span>
                                      <span>Rs. {(item.unit_price - item.line_discount - finalLineCOGS).toLocaleString('en-PK')}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Quantity & Pricing Controls */}
                      <div className="grid grid-cols-3 gap-2 pt-1 border-t border-brand-border text-xs">
                        <div>
                          <label className="block text-[9px] font-bold text-brand-muted uppercase">Qty</label>
                          {item.type === 'laptop' ? (
                            <span className="font-bold text-brand-black block py-1">1 (Serialized)</span>
                          ) : (
                            <input
                              type="number"
                              min={1}
                              max={item.available_qty}
                              value={item.quantity}
                              onChange={(e) => updateCartItem(item.id, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                              className="w-full px-2 py-1 bg-white border rounded font-semibold"
                            />
                          )}
                        </div>

                        <div>
                          <label className="block text-[9px] font-bold text-brand-muted uppercase">Final Price (PKR)</label>
                          <input
                            type="number"
                            min={0}
                            value={item.complimentary ? 0 : item.unit_price}
                            disabled={item.complimentary}
                            onChange={(e) => updateCartItem(item.id, { unit_price: Number(e.target.value) || 0 })}
                            className={`w-full px-2 py-1 border rounded font-bold text-brand-black ${item.complimentary ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : 'bg-white'}`}
                          />
                        </div>

                        <div>
                          <label className="block text-[9px] font-bold text-brand-muted uppercase">Line Discount</label>
                          <input
                            type="number"
                            min={0}
                            value={item.complimentary ? 0 : item.line_discount}
                            disabled={item.complimentary}
                            onChange={(e) => updateCartItem(item.id, { line_discount: Number(e.target.value) || 0 })}
                            className={`w-full px-2 py-1 border rounded font-semibold ${item.complimentary ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-white text-brand-red'}`}
                          />
                        </div>
                      </div>

                      {item.type === 'accessory' && (
                        <div className="flex items-center justify-between pt-1 border-t border-slate-100 text-[11px]">
                          <label className={`flex items-center space-x-1.5 ${canAddComplimentary ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
                            <input
                              type="checkbox"
                              checked={item.complimentary ?? false}
                              disabled={!canAddComplimentary}
                              onChange={(e) => {
                                const isComp = e.target.checked;
                                updateCartItem(item.id, {
                                  complimentary: isComp,
                                  unit_price: isComp ? 0 : (item.available_qty ? item.unit_price : 0),
                                  line_discount: 0,
                                });
                              }}
                              className="accent-blue-600 rounded"
                            />
                            <span className="font-semibold text-blue-700">
                              Complimentary / Free Gift (Rs. 0)
                            </span>
                          </label>
                          {item.complimentary && (
                            <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                              Shop Absorbed COGS: Rs. {(item.quantity * (item.unit_cost || 0)).toLocaleString('en-PK')}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Cart Pricing, Warranty, and Payment Controls */}
          {cart.length > 0 && (
            <div className="space-y-3 pt-3 border-t border-brand-border">
              {/* Overall Discount, Tax, Warranty */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[10px] font-bold text-brand-black uppercase">Overall Discount</label>
                  <input
                    type="number"
                    min={0}
                    value={overallDiscount}
                    onChange={(e) => setOverallDiscount(Number(e.target.value) || 0)}
                    className="w-full px-2 py-1 bg-brand-bg border rounded font-bold text-brand-red"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-brand-black uppercase">Tax (PKR)</label>
                  <input
                    type="number"
                    min={0}
                    value={tax}
                    onChange={(e) => setTax(Number(e.target.value) || 0)}
                    className="w-full px-2 py-1 bg-brand-bg border rounded font-semibold"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-brand-black uppercase flex items-center space-x-1">
                    <ShieldCheck className="w-3 h-3 text-brand-green" />
                    <span>Warranty (Mo)</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={60}
                    value={warrantyMonths}
                    onChange={(e) => setWarrantyMonths(Number(e.target.value) || 0)}
                    className="w-full px-2 py-1 bg-brand-bg border rounded font-bold"
                  />
                </div>
              </div>

              {/* Financial Summary & Role-Based Profit Preview */}
              <div className="p-3 bg-brand-bg rounded-lg border border-brand-border space-y-1">
                <div className="flex justify-between font-semibold">
                  <span>Subtotal:</span>
                  <span>Rs. {subtotal.toLocaleString('en-PK')}</span>
                </div>
                {overallDiscount > 0 && (
                  <div className="flex justify-between text-brand-red font-semibold">
                    <span>Discount:</span>
                    <span>- Rs. {overallDiscount.toLocaleString('en-PK')}</span>
                  </div>
                )}
                {tax > 0 && (
                  <div className="flex justify-between">
                    <span>Tax:</span>
                    <span>+ Rs. {tax.toLocaleString('en-PK')}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm font-black text-brand-black border-t pt-1">
                  <span>Total Payable:</span>
                  <span className="text-brand-green text-base">Rs. {total.toLocaleString('en-PK')}</span>
                </div>

                {canViewCosts && (
                  <div className="flex justify-between text-[10px] text-brand-muted border-t pt-1">
                    <span>Est. COGS: Rs. {totalCost.toLocaleString('en-PK')}</span>
                    <span className={estProfit >= 0 ? 'text-green-700 font-bold' : 'text-red-700 font-bold'}>
                      Est. Profit: Rs. {estProfit.toLocaleString('en-PK')}
                    </span>
                  </div>
                )}
              </div>

              {/* Payment Section */}
              <div className="grid grid-cols-2 gap-2 bg-green-50/60 p-2.5 rounded-lg border border-green-200">
                <div>
                  <label className="block text-[10px] font-bold text-brand-black uppercase">Payment Received</label>
                  <input
                    type="number"
                    placeholder={`Full (Rs. ${total})`}
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    className="w-full px-2 py-1 bg-white border rounded font-bold text-brand-green"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-brand-black uppercase">Payment Method</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full px-2 py-1 bg-white border rounded font-bold text-brand-black"
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile_wallet">Mobile Wallet</option>
                    <option value="card">Card</option>
                  </select>
                </div>
              </div>

              {/* Sale Notes */}
              <div>
                <label className="block text-[10px] font-bold text-brand-black uppercase mb-1">
                  Invoice Notes
                </label>
                <input
                  placeholder="e.g. Paid online, customer will collect charger later..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-1 bg-brand-bg border border-brand-border rounded text-xs"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-2 pt-2 border-t border-brand-border">
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={completeMutation.isPending}
                  className="px-3 py-2 text-xs font-semibold text-brand-muted hover:text-brand-black"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={completeMutation.isPending || cart.length === 0}
                  className="inline-flex items-center space-x-2 px-5 py-2 bg-brand-green text-white text-xs font-bold rounded-md hover:bg-brand-green-hover transition-colors shadow-md disabled:opacity-50"
                >
                  {completeMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  <span>{completeMutation.isPending ? 'Processing...' : 'Complete & Generate Invoice'}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showQrScanner && (
        <LaptopQrScannerModal
          isOpen={showQrScanner}
          onClose={() => setShowQrScanner(false)}
          onLaptopScanned={(laptop) => addLaptopToCart(laptop)}
          alreadyInCart={(id) => cart.some((c) => c.laptop_id === id)}
        />
      )}
    </form>
  );
};
